/**
 * Utility functions for handling the profile reminder feature
 * This manages timing, localStorage, and browser visibility for third-party new users
 */

/**
 * Checks if the user's profile is complete by verifying all required fields
 * @param {Object} userInfo - The user information object from Redux store
 * @returns {boolean} - True if profile is complete, false otherwise
 */
export const isProfileComplete = (userInfo) => {
  if (!userInfo) return true;
  
  return !!(
    userInfo.profile?.first_name &&
    userInfo.profile?.last_name &&
    userInfo.profile?.email &&
    userInfo.profile?.gender && 
    userInfo.profile?.birth_date && 
    userInfo.city &&
    userInfo.image
  );
};

/**
 * Get the user-specific localStorage keys for the given user
 * @param {string} uid - The user ID
 * @returns {Object} - Object containing all relevant keys
 */
export const getReminderKeys = (uid) => {
  return {
    creationDate: `user_creation_date_${uid}`,
    creationTimestamp: `user_creation_timestamp_${uid}`,
    reminderShown: `profile_reminder_shown_${uid}`,
    lastCheck: `last_reminder_check_${uid}`
  };
};

/**
 * Checks if the reminder should be shown on page load
 * @param {Object} userInfo - The user information object
 * @param {number} waitTime - The time to wait before showing reminder (in minutes)
 * @returns {boolean} - True if reminder should be shown, false otherwise
 */
export const shouldShowReminder = (userInfo) => {
  if (!userInfo || !userInfo.uid) return false;
  
  const waitTime = parseFloat(process.env.REACT_APP_EDIT_PROFILE_DELAY) || 2;
  const keys = getReminderKeys(userInfo.uid);
  
  // Get values from localStorage
  const userCreationDate = localStorage.getItem(keys.creationDate);
  const userCreationTimestamp = localStorage.getItem(keys.creationTimestamp);
  const reminderShown = localStorage.getItem(keys.reminderShown) === "true";
  
  // Update last check time
  localStorage.setItem(keys.lastCheck, Date.now().toString());
  
  // Only proceed if the user has a creation date, incomplete profile, and hasn't seen the reminder
  if (!userCreationDate || reminderShown || isProfileComplete(userInfo)) {
    return false;
  }
  
  try {
    // Calculate time since account creation
    let diffInMinutes;
    
    if (userCreationTimestamp) {
      // Use timestamp for more reliable calculation
      const creationTimeSecs = parseInt(userCreationTimestamp);
      const nowSecs = Math.floor(Date.now() / 1000);
      const diffInSecs = nowSecs - creationTimeSecs;
      diffInMinutes = diffInSecs / 60;
    } else {
      // Fall back to date object
      const creationDate = new Date(userCreationDate);
      const now = new Date();
      const diffInMs = now - creationDate;
      diffInMinutes = diffInMs / (1000 * 60);
    }
    
    // Check if enough time has passed
    return diffInMinutes >= waitTime;
  } catch (error) {
    console.error("Error calculating time for reminder:", error);
    return false;
  }
};

/**
 * Sets up event listeners and interval for checking the reminder status
 * @param {Object} userInfo - The user information object
 * @param {Function} showReminder - Function to call when reminder should be shown
 * @param {Function} cleanup - Function to call for cleanup on unmount
 */
export const setupReminderListeners = (userInfo, showReminder, cleanup) => {
  if (!userInfo || !userInfo.uid) return () => {};
  
  // Check function to determine if reminder should be shown
  const checkForReminder = () => {
    if (shouldShowReminder(userInfo)) {
      showReminder();
    }
  };
  
  // Handle visibility change
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      console.log('Tab became visible - checking reminder status');
      checkForReminder();
    }
  };
  
  // Handle online status change
  const handleOnline = () => {
    console.log('Device came back online - checking reminder status');
    checkForReminder();
  };
  
  // Handle window focus
  const handleFocus = () => {
    console.log('Window regained focus - checking reminder status');
    checkForReminder();
  };
  
  // Set up event listeners
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('online', handleOnline);
  window.addEventListener('focus', handleFocus);
  
  // Track last check time to detect large gaps
  let lastCheckTime = Date.now();
  
  // Set up interval
  const checkIntervalMs = 30000; // 30 seconds
  const checkInterval = setInterval(() => {
    const now = Date.now();
    const timeSinceLastCheck = now - lastCheckTime;
    
    // Detect long gaps (possibly due to sleep)
    if (timeSinceLastCheck > 120000) { // > 2 minutes
      console.log(`Large time gap detected (${Math.round(timeSinceLastCheck/1000)}s) - checking reminder status`);
    }
    
    checkForReminder();
    lastCheckTime = now;
  }, checkIntervalMs);
  
  // Return cleanup function
  return () => {
    clearInterval(checkInterval);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('focus', handleFocus);
    if (cleanup) cleanup();
  };
};

/**
 * Marks the reminder as shown for the given user
 * @param {string} uid - The user ID
 */
export const markReminderShown = (uid) => {
  if (!uid) return;
  const keys = getReminderKeys(uid);
  localStorage.setItem(keys.reminderShown, "true");
}; 